import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";
import SelectModeBanner from "../SelectModeBanner";

import EditIcon from "@mui/icons-material/Edit";
import ReplayIcon from "@mui/icons-material/Replay";
import PeopleIcon from "@mui/icons-material/People";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import AddCommentIcon from "@mui/icons-material/AddComment";

import { orderService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { ModalContext } from "../../Modals";
import ModalOrderManualChanges, {
  ModalOrderManualChangesProps,
} from "../../Modals/ModalOrderManualChanges/modal-order-manual-changes";
import { AclMatch, UserHasAcl, UserHasAcls } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import ModalSetFollowers, { ModalSetFollowersElementProps } from "../../Modals/ModalSetFollowers/modal-set-followers";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import { CancelOrdersByIds } from "../../../Redux/Reducers/redux-slice-object-orders";
import { useDispatch } from "react-redux";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import { OrderStatus } from "../../../Core/Api/Types/order-status";
import { CancelOutlined, ExpandMore, PlayArrowOutlined } from "@mui/icons-material";
import { ActivityKind } from "../../../Core/Api/Types/activity-kind";
import ModalActivityAddComment, {
  ModalActivityAddCommentElementProps,
} from "../../Modals/ModalActivityAddComment/modal-activity-add-comment";
import { useTranslation } from "react-i18next";

export interface OrderSelectModeBannerProps {
  handleCloseClick: React.MouseEventHandler;
  selectedItems: DbOrderItemInfos[];
}

enum TopbarAction {
  Play,
  Cancel,
  Retry,
  Modify,
  ApplyManualChanges,
  Followers,
  Copy,
  RetryFromScratch,
  Comment,
}

// We talk here about order items selection
const OrderSelectModeBanner: React.FC<OrderSelectModeBannerProps> = ({ handleCloseClick, selectedItems }) => {
  const { t } = useTranslation();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const dispatch = useDispatch();

  const { modifyObjectByIdToBasket, copyObjectByIdToBasket } = useBasketActions();

  const handleAction = (action: TopbarAction) => {
    if (action === TopbarAction.ApplyManualChanges) {
      openModal &&
        openModal<ModalOrderManualChangesProps>(ModalOrderManualChanges, {
          closeModal: () => false,
          itemIds: selectedItems.map((x) => x.id),
        });

      return;
    } else if (action === TopbarAction.Followers) {
      openModal &&
        openModal<ModalSetFollowersElementProps>(ModalSetFollowers, {
          objectIds: selectedItems.map((x) => x.id),
          objectType: "orderItem",
          currentFollowersIds: [],
        });

      return;
    } else if (action === TopbarAction.Comment) {
      openModal &&
        openModal<ModalActivityAddCommentElementProps>(ModalActivityAddComment, {
          objectIds: selectedItems.map((x) => x.id),
          kind: ActivityKind.OrderItem,
        });

      return;
    }

    selectedItems.map((item) => {
      switch (action) {
        case TopbarAction.Play:
          orderService.PlayOrderItemById(item.id).then((response) => {
            if (response.success)
              enqueueCustomSnackbar(t("alert:success.itemIdPlayed", { itemId: item.id }), "success");
            else
              enqueueCustomSnackbar(
                t("alert:error.itemIdPlay", { itemId: item.id, message: response.message }),
                "error"
              );
          });
          break;
        case TopbarAction.Cancel:
          orderService.CancelOrderItemById(item.id).then((response) => {
            if (response.success) {
              enqueueCustomSnackbar(t("alert:success.itemIdCancelled", { itemId: item.id }), "success");
              dispatch(CancelOrdersByIds([item.id]));
            } else
              enqueueCustomSnackbar(
                t("alert:error.itemIdCancel", { itemId: item.id, message: response.message }),
                "error"
              );
          });
          break;
        case TopbarAction.Retry:
          orderService.RetryOrderItemById(item.id).then((response) => {
            if (response.success)
              enqueueCustomSnackbar(t("alert:success.itemIdRetried", { itemId: item.id }), "success");
            else
              enqueueCustomSnackbar(
                t("alert:error.itemIdRetry", { itemId: item.id, message: response.message }),
                "error"
              );
          });
          break;
        case TopbarAction.RetryFromScratch:
          orderService.RetryFromScratchOrderItemById(item.id).then((response) => {
            if (response.success)
              enqueueCustomSnackbar(t("alert:success.itemIdRetried", { itemId: item.id }), "success");
            else
              enqueueCustomSnackbar(
                t("alert:error.itemIdRetry", { itemId: item.id, message: response.message }),
                "error"
              );
          });
          break;
        case TopbarAction.Modify:
          modifyObjectByIdToBasket(item.id);
          break;
        case TopbarAction.Copy:
          copyObjectByIdToBasket(item.id);
          break;
        default:
          console.error("Action not handle yet: " + action);
          break;
      }
    });
  };

  const [anchorMoreEl, setAnchorMoreEl] = useState<null | HTMLElement>(null);
  const moreOpen = Boolean(anchorMoreEl);
  const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorMoreEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setAnchorMoreEl(null);
  };

  const [anchorPlayEl, setAnchorPlayEl] = useState<null | HTMLElement>(null);
  const playOpen = Boolean(anchorPlayEl);
  const handlePlayClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorPlayEl(event.currentTarget);
  };
  const handlePlayClose = () => {
    setAnchorPlayEl(null);
  };

  return (
    <SelectModeBanner
      text={t("banner:body.configurationSelected", { count: selectedItems.length })}
      onCloseClick={handleCloseClick}
    >
      {/*<Button sx={{ mr: 2 }} startIcon={<ReportOutlinedIcon />} disabled={true} color="error" variant="contained">
        Report
  </Button>*/}
      <Button
        variant="whiteSoft"
        startIcon={<EditIcon />}
        onClick={() => handleAction(TopbarAction.Modify)}
        disabled={
          !UserHasAcl(AclTypes.aclOrderUpdate) || selectedItems.filter((x) => x.status !== OrderStatus.READY).length > 0
        }
      >
        {t("common:editUpperCase")}
      </Button>

      <Button
        aria-controls={moreOpen ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={moreOpen ? "true" : undefined}
        onClick={handlePlayClick}
        variant="whiteSoft"
        size="small"
        endIcon={<ExpandMore />}
      >
        {t("common:actions")}
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorPlayEl}
        open={playOpen}
        onClose={handlePlayClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem onClick={() => handleAction(TopbarAction.Play)} disabled={!UserHasAcl(AclTypes.aclOrderStart)}>
          <ListItemIcon>
            <PlayArrowOutlined />
          </ListItemIcon>
          <ListItemText>{t("common:play")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction(TopbarAction.Cancel)} disabled={!UserHasAcl(AclTypes.aclOrderCancel)}>
          <ListItemIcon>
            <CancelOutlined />
          </ListItemIcon>
          <ListItemText>{t("common:cancel")}</ListItemText>
        </MenuItem>
        {(UserHasAcl(AclTypes.aclAdmin) || UserHasAcl(AclTypes.aclSuperUser)) && (
          <MenuItem onClick={() => handleAction(TopbarAction.Retry)} disabled={!UserHasAcl(AclTypes.aclOrderStart)}>
            <ListItemIcon>
              <ReplayIcon />
            </ListItemIcon>
            <ListItemText>{t("common:retry")}</ListItemText>
          </MenuItem>
        )}
        {UserHasAcl(AclTypes.aclSuperUser) && (
          <MenuItem
            onClick={() => handleAction(TopbarAction.RetryFromScratch)}
            disabled={!UserHasAcl(AclTypes.aclOrderStart)}
          >
            <ListItemIcon>
              <ReplayIcon />
            </ListItemIcon>
            <ListItemText>{t("common:retryFromScratch")}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Button
        aria-controls={moreOpen ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={moreOpen ? "true" : undefined}
        onClick={handleMoreClick}
        variant="whiteSoft"
        size="small"
        endIcon={<ExpandMore />}
      >
        {t("common:more")}
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorMoreEl}
        open={moreOpen}
        onClose={handleMoreClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          onClick={() => handleAction(TopbarAction.Followers)}
          disabled={!UserHasAcl(AclTypes.aclOrderAssignFollowers)}
        >
          <ListItemIcon>
            <PeopleIcon />
          </ListItemIcon>
          <ListItemText>{t("common:setFollowers")}</ListItemText>
        </MenuItem>
        {UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclOrderManualTaskExternalChangeStatus], AclMatch.OneAtLeast) && (
          <MenuItem onClick={() => handleAction(TopbarAction.ApplyManualChanges)}>
            <ListItemIcon>
              <AutoFixHighIcon />
            </ListItemIcon>
            <ListItemText>{t("common:applyManualChanges")}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleAction(TopbarAction.Comment)} disabled={!UserHasAcl(AclTypes.aclActivityAdd)}>
          <ListItemIcon>
            <AddCommentIcon />
          </ListItemIcon>
          <ListItemText>{t("modal:title.addComment")}</ListItemText>
        </MenuItem>
      </Menu>

      <Divider
        orientation="vertical"
        flexItem
        sx={(theme) => ({ borderColor: theme.palette.backgroundBoldAlpha.border, m: 0.5 })}
      />

      <Button
        variant="white"
        startIcon={<ShoppingBagOutlinedIcon />}
        onClick={() => handleAction(TopbarAction.Copy)}
        disabled={!UserHasAcl(AclTypes.aclSectionOrderAccess)}
      >
        {t("button:orderAgain")}
      </Button>
    </SelectModeBanner>
  );
};

export default OrderSelectModeBanner;
